import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Layers, Play, SlidersHorizontal } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import { ImportStatusChip, formatDate } from "./shared";
import BulkImportCard from "./BulkImportCard";

/**
 * GIS Imports: turn a sheet's vector layers (zoning, villages, survey
 * parcels, TP plots, roads) into map layers.
 *
 *   /gis-imports       choose a sheet; recent imports
 *   /gis-imports/:id   match layers and colours, build a draft, preview, publish
 *
 * Two kinds of sheet work: GeoPDFs whose coordinates GDAL can read (ArcMap
 * exports such as Bardoli DP 2037), and plain vector PDFs that have been
 * georeferenced as rasters (AUDA, VUDA), placed with their control points.
 * The backend is R-Point modules/v1/admin/maps/gis-imports.
 */
export default function GisImportList() {
  const navigate = useNavigate();
  const toast = useToast();

  const [mapTypes, setMapTypes] = useState([]);
  const [mapTypeId, setMapTypeId] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [location, setLocation] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [authority, setAuthority] = useState("");
  const [planName, setPlanName] = useState("");
  const [starting, setStarting] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Axios.get("/maps/map-types/all?is_active=true", authHeaders())
      .then((res) => {
        const types = res.data?.data || [];
        setMapTypes(types);
        // Development plans are where zoning comes from; start there.
        const dp = types.find((t) => t.code?.toLowerCase() === "dp");
        if (dp) setMapTypeId((current) => current || String(dp.id));
      })
      .catch((err) => console.error("Failed to fetch map types", err));

    Axios.get("/maps/gis-imports", { params: { limit: 20 }, ...authHeaders() })
      .then((res) => setRecent(res.data?.data?.data || []))
      .catch((err) => console.error("Failed to fetch GIS imports", err));
  }, []);

  useEffect(() => {
    if (!mapTypeId) {
      setLocationOptions([]);
      return undefined;
    }
    const handler = setTimeout(async () => {
      try {
        const res = await Axios.get("/maps/map-locations/all", {
          params: { map_type_id: mapTypeId, search: locationSearch, last_level_only: true, page: 1, limit: 50 },
          ...authHeaders(),
        });
        setLocationOptions(
          (res.data?.data || []).map((l) => ({ value: l.id, label: l.location_path || l.name, name: l.name }))
        );
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [mapTypeId, locationSearch]);

  useEffect(() => {
    setSheet(null);
    if (!location) {
      setSheets([]);
      return;
    }
    setLoadingSheets(true);
    Axios.get("/maps/gis-imports/sheets", { params: { location_id: location.value }, ...authHeaders() })
      .then((res) => setSheets(res.data?.data || []))
      .catch((err) => toast.error(err.response?.data?.message || "Could not load the sheets"))
      .finally(() => setLoadingSheets(false));
  }, [location, toast]);

  const mapTypeOptions = useMemo(
    () => mapTypes.map((t) => ({ value: String(t.id), label: `${t.name} (${t.code?.toUpperCase()})` })),
    [mapTypes]
  );
  const sheetOptions = useMemo(
    () =>
      sheets.map((s) => ({
        value: s.id,
        label: `${s.title}${s.import_status ? ` — last import: ${s.import_status}` : ""}`,
        sheet: s,
      })),
    [sheets]
  );

  const start = async (e) => {
    e.preventDefault();
    if (!sheet) return;
    setStarting(true);
    try {
      const res = await Axios.post(
        "/maps/gis-imports",
        { map_id: sheet.value, authority: authority.trim() || undefined, plan_name: planName.trim() || undefined },
        authHeaders()
      );
      navigate(`/gis-imports/${res.data?.data?.id}`);
    } catch (err) {
      const existing = err.response?.data?.data?.import_id;
      if (err.response?.status === 409 && existing) {
        toast.info("This sheet already has an import in progress. Opening it.");
        navigate(`/gis-imports/${existing}`);
        return;
      }
      toast.error(err.response?.data?.message || "Could not start the import");
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
            <Layers size={18} /> GIS layers from a PDF sheet
          </h5>
          <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1" onClick={() => navigate("/gis-imports/profiles")}>
            <SlidersHorizontal size={14} /> Import profiles
          </Button>
        </div>
        <p className="text-muted mb-4" style={{ maxWidth: 760 }}>
          Choose a vector PDF sheet: a GeoPDF (for example exported from ArcMap), or a plain PDF that has already been
          georeferenced here, such as the AUDA and VUDA DP sheets, which is placed with its control points. Its layers
          are read and described; you then pick which layer is zoning, villages, survey numbers, TP plots and roads,
          and which colour is which zone. Everything is built as a draft you can preview before it goes on the map.
        </p>

        <Form onSubmit={start} style={{ maxWidth: 760 }}>
          <div className="row g-3">
            <div className="col-md-6">
              <CustomSelect
                label={{ name: "Map type" }}
                options={mapTypeOptions}
                value={mapTypeOptions.find((o) => o.value === mapTypeId) || null}
                onChange={(o) => {
                  setMapTypeId(o?.value || "");
                  setLocation(null);
                }}
                placeholder="Choose a map type"
              />
            </div>
            <div className="col-md-6">
              <CustomSelect
                label={{ name: "Location" }}
                isDisabled={!mapTypeId}
                options={locationOptions}
                value={location}
                onChange={(o) => setLocation(o || null)}
                onInputChange={(v, meta) => {
                  if (meta.action === "input-change") setLocationSearch(v);
                }}
                placeholder="Search a location"
                isClearable
              />
            </div>
            <div className="col-12">
              <CustomSelect
                label={{ name: "Sheet (PDF)" }}
                required
                isDisabled={!location}
                isLoading={loadingSheets}
                options={sheetOptions}
                value={sheet}
                onChange={(o) => {
                  setSheet(o || null);
                  if (o && !planName) setPlanName(o.sheet.title);
                }}
                placeholder={location ? "Choose a sheet" : "Choose a location first"}
                noOptionsMessage={() => (location ? "No PDF sheets here" : "Choose a location first")}
              />
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="small fw-medium">Authority</Form.Label>
                <Form.Control
                  value={authority}
                  onChange={(e) => setAuthority(e.target.value.toUpperCase())}
                  placeholder="e.g. BUDA, AUDA, VUDA"
                  maxLength={30}
                />
                <Form.Text muted>Zone colours are remembered per authority.</Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-8">
              <Form.Group>
                <Form.Label className="small fw-medium">Plan name</Form.Label>
                <Form.Control value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="e.g. Bardoli DP 2037" />
              </Form.Group>
            </div>
          </div>

          <Button type="submit" className="mt-4 d-flex align-items-center gap-2" disabled={!sheet || starting}>
            {starting ? <Spinner size="sm" /> : <Play size={16} />}
            Read the sheet
          </Button>
        </Form>
      </div>

      <BulkImportCard
        mapTypeOptions={mapTypeOptions}
        defaultMapTypeId={String(mapTypes.find((t) => t.code?.toLowerCase() === "dp")?.id || "")}
      />

      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <h6 className="fw-semibold mb-3">Recent imports</h6>
        {recent.length === 0 ? (
          <p className="text-muted mb-0">No imports yet.</p>
        ) : (
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Plan</th>
                <th>Started</th>
                <th>Status</th>
                <th>Zones</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/gis-imports/${r.id}`)}>
                  <td className="fw-medium">
                    {r.map?.title || "-"} <span className="text-muted small">({r.map?.location_name || "-"})</span>
                  </td>
                  <td>
                    {r.plan_name || "-"} {r.authority && <span className="text-muted small">· {r.authority}</span>}
                  </td>
                  <td>{formatDate(r.created_at)}</td>
                  <td>
                    <ImportStatusChip status={r.status} />
                  </td>
                  <td className="text-muted small">{r.summary?.dp_zones != null ? r.summary.dp_zones.toLocaleString("en-IN") : "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {recent.some((r) => r.status === "failed") && (
        <Alert variant="light" className="mt-3 small text-muted">
          A failed import can be opened to see why, then discarded.
        </Alert>
      )}
    </>
  );
}
